import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { Logo } from "../components/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import {
  ShieldCheck,
  Building2,
  Wrench,
  ArrowLeft,
  Loader2,
  Lock,
  UserRound,
} from "lucide-react";

const ROLES = [
  {
    key: "society",
    label: "Society Login",
    Icon: Building2,
    tone: "text-primary bg-primary/10",
    desc: "Manage your society — accounting, members, notices & more.",
    canRegister: false,
  },
  {
    key: "resident",
    label: "Resident Login",
    Icon: UserRound,
    tone: "text-emerald-700 bg-emerald-50",
    desc: "View maintenance, receipts, notices and society services.",
    canRegister: false,
  },
  {
    key: "provider",
    label: "Vendor / Technician / Expert",
    Icon: Wrench,
    tone: "text-secondary bg-secondary/10",
    desc: "Use approved credentials or apply to join the service network.",
    canRegister: true,
  },
];

function errText(e) {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
}

export default function Login() {
  const { user, loading, setUser, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [role, setRole] = useState(null); // 'society' | 'vendor' | 'expert' | 'admin'
  const [mode, setMode] = useState("login"); // 'login' | 'register'
  const [form, setForm] = useState({ name: "", email: "", password: "" });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!loading && user) {
      if (!user.onboarded || !user.role) navigate("/onboarding");
      else if (user.role === "super_admin" || user.role === "staff") navigate("/admin");
      else if (user.role === "vendor") navigate("/vendor");
      else if (user.role === "expert") navigate("/expert");
      else if (user.role === "society_member") navigate("/resident");
      else if (user.role === "society_portal") navigate("/app/setup");
      else navigate("/app");
    }
  }, [user, loading, navigate]);

  const isAdmin = role === "admin";
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    if (role === "provider" && mode === "register") {
      navigate("/join-provider");
      return;
    }
    if (!form.email || !form.password) { toast.error("Enter email and password"); return; }
    setBusy(true);
    try {
      if (mode === "register") {
        if (!form.name) { toast.error("Please enter your name"); setBusy(false); return; }
        const r = await api.post("/auth/register", { ...form, role });
        try { sessionStorage.setItem("msm_onboard_type", role); } catch {}
        setUser(r.data.user);
        await checkAuth();
        toast.success("Account created! Let's complete your application.");
      } else {
        const r = await api.post("/auth/login", {
          email: form.email,
          password: form.password,
          portal: role,
        });
        setUser(r.data.user);
        await checkAuth();
        toast.success("Welcome back!");
      }
    } catch (e) {
      toast.error(errText(e));
    } finally { setBusy(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-primary" /></div>;

  const active = ROLES.find((r) => r.key === role);
  const canRegister = Boolean(active?.canRegister);

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between bg-primary p-12 text-white relative overflow-hidden">
        <div className="relative z-10"><Logo light /></div>
        <div className="relative z-10 max-w-md">
          <h1 className="font-display text-4xl font-bold tracking-tight leading-tight">
            Your Society. Our Responsibility. One Trusted Partner.
          </h1>
          <p className="mt-4 text-white/70 leading-relaxed">
            Manage accounting, documents, projects, vendors, AMCs and expert support from one simple platform.
          </p>
          <div className="mt-8 space-y-3">
            {["Bank-grade tenant isolation", "Double-entry society accounting", "AI-assisted notices & minutes"].map((t) => (
              <div key={t} className="flex items-center gap-3 text-white/90"><ShieldCheck className="w-5 h-5 text-secondary" /> {t}</div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-white/50 text-sm">Boisar · Palghar · Maharashtra · India</div>
        <div className="absolute -bottom-24 -right-24 w-96 h-96 rounded-full bg-secondary/20 blur-3xl" />
        <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-white/10 blur-3xl" />
      </div>

      {/* Auth panel */}
      <div className="flex flex-col items-center justify-center p-8 bg-background relative">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center"><Logo /></div>

          {!role && (
            <div data-testid="role-picker">
              <h2 className="font-display text-3xl font-bold tracking-tight">Welcome to MySocietyMitra</h2>
              <p className="text-muted-foreground mt-2">Choose how you'd like to sign in.</p>
              <div className="grid gap-4 mt-8">
                {ROLES.map(({ key, label, Icon, tone, desc }) => (
                  <button key={key} data-testid={`role-card-${key}`} onClick={() => { setRole(key); setMode("login"); }}
                    className="flex items-center gap-4 text-left bg-card border rounded-2xl p-5 hover:-translate-y-0.5 hover:shadow-lg transition-all">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center shrink-0 ${tone}`}><Icon className="w-6 h-6" /></div>
                    <div>
                      <div className="font-display font-bold text-lg">{label}</div>
                      <div className="text-sm text-muted-foreground">{desc}</div>
                    </div>
                  </button>
                ))}
              </div>
              <div className="mt-8 text-center">
                <button data-testid="super-admin-link" onClick={() => { setRole("admin"); setMode("login"); }}
                  className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors">
                  <Lock className="w-3.5 h-3.5" /> Super Admin login
                </button>
              </div>
            </div>
          )}

          {role && (
            <div data-testid="auth-form">
              <button onClick={() => { setRole(null); setForm({ name: "", email: "", password: "" }); }}
                className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-primary mb-6" data-testid="auth-back">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <div className="flex items-center gap-3 mb-2">
                <div className={`w-11 h-11 rounded-xl flex items-center justify-center ${isAdmin ? "text-primary bg-primary/10" : active.tone}`}>
                  {isAdmin ? <Lock className="w-5 h-5" /> : (() => { const I = active.Icon; return <I className="w-5 h-5" />; })()}
                </div>
                <div>
                  <h2 className="font-display text-2xl font-bold tracking-tight">{isAdmin ? "Super Admin" : active.label}</h2>
                  <p className="text-sm text-muted-foreground">{role === "provider" && mode === "register" ? "Apply to join the verified service network" : mode === "login" ? "Sign in to your account" : "Create your account"}</p>
                </div>
              </div>

              <div className="mt-6 space-y-4">
                {mode === "register" && !isAdmin && role !== "provider" && (
                  <div><Label className="mb-1.5 block text-sm">Full Name</Label>
                    <Input data-testid="auth-name" value={form.name} onChange={(e) => set("name", e.target.value)} placeholder="Your name" /></div>
                )}
                {!(role === "provider" && mode === "register") && <div><Label className="mb-1.5 block text-sm">Email</Label>
                  <Input data-testid="auth-email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} placeholder="you@example.com" /></div>
                }
                {!(role === "provider" && mode === "register") && <div><Label className="mb-1.5 block text-sm">Password</Label>
                  <Input data-testid="auth-password" type="password" value={form.password} onChange={(e) => set("password", e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && submit()} placeholder="••••••••" /></div>
                }

                {role === "resident" && (
                  <p
                    className="border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs leading-5 text-emerald-950"
                    data-testid="resident-login-help"
                  >
                    Use your member email and the password you created from your
                    Society Admin&apos;s activation email. There is no default password.
                  </p>
                )}

                <Button data-testid="auth-submit" onClick={submit} disabled={busy} className="w-full h-11 rounded-full text-base gap-2">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
                  {role === "provider" && mode === "register" ? "Register Here" : mode === "login" ? "Log In" : "Create Account"}
                </Button>

                {!isAdmin && canRegister && (
                  <p className="text-sm text-center text-muted-foreground">
                    {mode === "login" ? "New here? " : "Already have credentials? "}
                    <button data-testid="auth-toggle-mode" onClick={() => setMode(mode === "login" ? "register" : "login")}
                      className="font-semibold text-primary hover:underline">
                      {mode === "login" ? "Register Here" : "Login Here"}
                    </button>
                  </p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
